// Ultrasound calibration from the Sequence of Ultrasound Regions (0018,6011).
//
// Most ultrasound images carry no Pixel Spacing (0028,0030): the scanner states the physical size of a pixel
// per region of the image instead. A 2D tissue/flow region whose X and Y units are centimetres gives the
// spacing of the image in mm, so lengths and areas measured on it read in mm instead of pixels.
//
// Pure functions (no Cornerstone, no DOM) so they can be unit-tested; the metadata providers use them.

import type { DicomJson } from './dicomweb';

export const US_REGIONS_TAG = '00186011';

/** Fields of one region item, by DICOM tag. */
export const US_REGION_TAG = {
  minX: '00186018',
  minY: '0018601A',
  maxX: '0018601C',
  maxY: '0018601E',
  spatialFormat: '00186012',
  unitsX: '00186024',
  unitsY: '00186026',
  deltaX: '0018602C',
  deltaY: '0018602E',
} as const;

type RegionField = keyof typeof US_REGION_TAG;
export type RegionVr = 'UL' | 'US' | 'FD';

/** Value representation of each field: UL bounds, US codes, FD deltas. */
export const US_REGION_VR: Record<RegionField, RegionVr> = {
  minX: 'UL', minY: 'UL', maxX: 'UL', maxY: 'UL', spatialFormat: 'US', unitsX: 'US', unitsY: 'US', deltaX: 'FD', deltaY: 'FD',
};

/** Region Spatial Format 1 = 2D (tissue or flow); M-mode and spectral regions mix time into one axis. */
const SPATIAL_2D = 1;
/** Physical Units code 3 = cm. */
const UNITS_CM = 3;
const MM_PER_CM = 10;
const SAME_DELTA_TOLERANCE = 1e-6;

export type UsRegion = Record<RegionField, number | undefined>;

export interface UsCalibration {
  rowPixelSpacing: number;
  columnPixelSpacing: number;
  /** How many calibrated 2D regions the image has; the chosen one is the largest when they disagree. */
  regions: number;
}

/** Reads one region from any item that can answer a numeric value by tag and VR (DICOM JSON or a dicomParser item). */
export function regionFrom(read: (tag: string, vr: RegionVr) => number | undefined): UsRegion {
  const out = {} as UsRegion;
  for (const [field, tag] of Object.entries(US_REGION_TAG) as Array<[RegionField, string]>) out[field] = read(tag, US_REGION_VR[field]);
  return out;
}

function jsonNumber(item: DicomJson, tag: string): number | undefined {
  const value = item[tag]?.Value?.[0];
  const n = typeof value === 'number' ? value : Number(value);
  return value === undefined || value === null || !Number.isFinite(n) ? undefined : n;
}

/** The regions of a DICOM JSON instance (WADO-RS metadata); [] when it has none. */
export function regionsFromJson(json: DicomJson | undefined): UsRegion[] {
  const items = (json?.[US_REGIONS_TAG]?.Value ?? []) as DicomJson[];
  return items.filter((item) => item && typeof item === 'object').map((item) => regionFrom((tag) => jsonNumber(item, tag)));
}

function calibratable(region: UsRegion): boolean {
  const { spatialFormat, unitsX, unitsY, deltaX, deltaY } = region;
  return spatialFormat === SPATIAL_2D && unitsX === UNITS_CM && unitsY === UNITS_CM &&
    deltaX !== undefined && deltaY !== undefined && Math.abs(deltaX) > 0 && Math.abs(deltaY) > 0;
}

function area(region: UsRegion): number {
  const { minX = 0, minY = 0, maxX = 0, maxY = 0 } = region;
  return Math.max(0, maxX - minX) * Math.max(0, maxY - minY);
}

function sameDeltas(a: UsRegion, b: UsRegion): boolean {
  const close = (x = 0, y = 0) => Math.abs(Math.abs(x) - Math.abs(y)) <= SAME_DELTA_TOLERANCE * Math.max(Math.abs(x), Math.abs(y));
  return close(a.deltaX, b.deltaX) && close(a.deltaY, b.deltaY);
}

/**
 * Pixel spacing in mm from the calibrated 2D regions, or undefined when there is none. When several regions
 * disagree (dual view at different depths) the largest one wins: a measurement in a smaller region then reads
 * with that scale, as the region a measurement sits in is not known at this level.
 */
export function calibrationFrom(regions: UsRegion[]): UsCalibration | undefined {
  const usable = regions.filter(calibratable);
  if (!usable.length) return undefined;
  const chosen = usable.every((r) => sameDeltas(r, usable[0])) ? usable[0] : [...usable].sort((a, b) => area(b) - area(a))[0];
  return {
    rowPixelSpacing: Math.abs(chosen.deltaY ?? 0) * MM_PER_CM,
    columnPixelSpacing: Math.abs(chosen.deltaX ?? 0) * MM_PER_CM,
    regions: usable.length,
  };
}
